// ── 대시보드 기본 데이터 훅 ───────────────────────────────────────────────────
// 상태 선언, 데이터 로드 효과, 기본 API 함수 포함
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../api/apiClient';
import { nowKST } from '../../utils/date';
import { MOBILE_BREAKPOINT } from './utils';
import { fetchCurrentAddress as fetchAddressGeo } from './dashboardGeo';

/**
 * 대시보드 기본 상태 + 데이터 로드 훅
 * @param {{ navigate: function, showToast: function, tabOrder: Array|null }} params
 */
export function useDashboardBase({ navigate, showToast }) {
  // ── KST 현재 연도/월 계산 ────────────────────────────────────────────────
  // nowKST: utils/date.js 사용 (new Date 직접 사용 금지)
  const nowKst       = useMemo(() => nowKST(), []);
  const currentYear  = nowKst.getUTCFullYear();
  const currentMonth = nowKst.getUTCMonth() + 1;

  // ── 위장 접속(Impersonation) 모드 여부 ───────────────────────────────────
  const [isImpersonating] = useState(() => apiClient.isImpersonating());

  // ── 탭 선택 상태: URL ?tab= 파라미터에서 복원 ─────────────────────────
  const [menu, setMenu] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'attendance';
  });

  const [isMobile, setIsMobile]           = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const accountMenuRef                    = useRef(null);   // 계정 메뉴 외부 클릭 감지용 ref
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // ── 외부 회사 목록 ────────────────────────────────────────────────────
  const [externalCompanies, setExternalCompanies] = useState([]);
  // 로딩 완료 전에 권한 가드가 ext- 탭을 잘못 판단하지 않도록 보호
  const [extCompaniesLoaded, setExtCompaniesLoaded] = useState(false);

  // ── 사용자/권한 상태 ──────────────────────────────────────────────────
  const [permCodesSet, setPermCodesSet]     = useState(null);  // 새 통합 권한 시스템 Set<string>
  const [taskCodes, setTaskCodes]           = useState([]);
  const [featureSetNames, setFeatureSetNames] = useState([]);
  const [me, setMe]                         = useState(null);
  const [myIp, setMyIp]                     = useState('');
  const [ipAllowed, setIpAllowed]           = useState(false);
  // 상단 카드용 직원 요약 — /api/check-ip 에서 온다 (as_user_id 를 타므로 열람 대상 기준).
  //   ⚠ /api/me 는 사이드바용이라 as_user_id 가 안 붙어(관리자 본인) 여기에 못 쓴다.
  const [attendanceProfile, setAttendanceProfile] = useState(null);
  // 출퇴근 기록이 있는 연도만 (연도 셀렉트용)
  const [attendanceYears, setAttendanceYears] = useState([]);

  const [ipCheckInfo, setIpCheckInfo]       = useState({
    mismatch_message: '', guidance: '', expected_ip: null, matched_ip: null,
  });
  const [today, setToday]                   = useState(null);
  const [baseLoading, setBaseLoading]       = useState(false);
  // 권한 로딩 완료 여부: URL 탭 복원 타이밍 제어
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [actionLoading, setActionLoading]   = useState(false);

  // ── 출퇴근 기록 상태 ──────────────────────────────────────────────────
  const [attendanceYear, setAttendanceYear]         = useState(currentYear);
  const [attendanceMonth, setAttendanceMonth]       = useState(currentMonth);
  const [attendanceList, setAttendanceList]         = useState([]);
  const [attendanceLoading, setAttendanceLoading]   = useState(false);
  const [expandedAttendance, setExpandedAttendance] = useState({});
  const [breakDraftById, setBreakDraftById]         = useState({});
  const [savingBreakById, setSavingBreakById]       = useState({});

  // ── 연차 상태 ─────────────────────────────────────────────────────────
  const [leaveData, setLeaveData] = useState({
    profile: { name: '', hire_date: null },
    summary: { generated: 0, used: 0, paid_recognized: 0, settled: 0, remaining: 0, unpaid: 0 },
    list: [],
  });
  const [leaveLoading, setLeaveLoading]             = useState(false);
  const [leaveLoaded, setLeaveLoaded]               = useState(false);
  const [expandedLeave, setExpandedLeave]           = useState({});
  const [activeLeaveFilters, setActiveLeaveFilters] = useState([]);

  // ── 위치/주소 상태 ───────────────────────────────────────────────────
  const [currentAddress, setCurrentAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [locationUsable, setLocationUsable] = useState(false);

  // ── 비밀번호 변경 모달 상태 ──────────────────────────────────────────
  const [pwModal, setPwModal]             = useState(false);
  const [forcePwChange, setForcePwChange] = useState(false);
  const [pwForm, setPwForm]               = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  // ── 지각 사유 모달 상태 ──────────────────────────────────────────────
  const [lateReasonModalOpen, setLateReasonModalOpen] = useState(false);
  const [lateReasonText, setLateReasonText]           = useState('');
  const [latePromptInfo, setLatePromptInfo]           = useState({
    pending_address: null, late_minutes: null, schedule_start_time: null,
  });

  // ── 연도/월 선택 옵션 ────────────────────────────────────────────────
  const yearOptions = useMemo(() => {
    const minYear = Math.max(currentYear - 10, 2019);
    const years = [];
    for (let y = currentYear; y >= minYear; y -= 1) years.push(y);
    return years;
  }, [currentYear]);
  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  // ── useEffect: 화면 크기 변화 감지 ──────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── useEffect: 계정 메뉴 외부 클릭 시 닫기 ───────────────────────────
  useEffect(() => {
    function handleClickOutside(event) {
      if (!accountMenuRef.current) return;
      if (!accountMenuRef.current.contains(event.target)) setAccountMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── useEffect: URL ?imp_token= 파라미터 감지 → sessionStorage 저장 후 URL 정리
  useEffect(() => {
    const params   = new URLSearchParams(window.location.search);
    const impToken = params.get('imp_token');
    if (impToken) {
      apiClient.initImpersonation(impToken);
      params.delete('imp_token');
      const newSearch = params.toString();
      const newUrl    = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState(null, '', newUrl);
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── useEffect: 최초 마운트 시 기본 데이터 로드 ───────────────────────
  useEffect(() => {
    fetchBase();
    fetchCurrentAddress();
    refreshExternalCompanies();
    // 동적 관리 페이지(TicketTypeManagement)에서 외부 회사 저장 시 사이드바 탭 즉시 갱신
    window.addEventListener('refreshExternalCompanies', refreshExternalCompanies);
    return () => window.removeEventListener('refreshExternalCompanies', refreshExternalCompanies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── useEffect: 동적 관리 탭을 떠날 때 외부 회사 목록 재조회 ───────────
  const prevMenuRef = useRef(null);
  useEffect(() => {
    if (prevMenuRef.current === 'ticket-types' && menu !== 'ticket-types') {
      refreshExternalCompanies();
    }
    prevMenuRef.current = menu;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  // ── 탭 전환 헬퍼: menu 상태 + URL ?tab= 동시 업데이트 ───────────────
  function openMenu(key) {
    navigate('?tab=' + key, { replace: true });
    setMenu(key);
  }

  // ── 외부 회사 목록 재조회 (동적 관리 저장 후 이벤트로도 호출됨) ─────
  function refreshExternalCompanies() {
    apiClient.get('/api/my/external-companies')
      .then((res) => { if (res.ok && res.data?.list) setExternalCompanies(res.data.list); })
      .catch(() => {})
      .finally(() => setExtCompaniesLoaded(true));
  }

  // ── 기본 데이터 로드: 사용자 정보, IP, 오늘 출퇴근, 권한 ─────────────
  async function fetchBase() {
    setBaseLoading(true);
    try {
      const [meRes, ipRes, checkIpRes, todayRes, taskRes, permRes] = await Promise.all([
        apiClient.get('/api/me'),
        apiClient.get('/api/my-ip'),
        apiClient.get('/api/check-ip'),
        apiClient.get('/api/attendance/today'),
        apiClient.get('/api/my/work-feature-assignments'),
        apiClient.get('/api/my/permissions'),
      ]);
      if (!meRes.ok) {
        // 위장 접속 중 토큰 만료 시: sessionStorage만 제거
        if (apiClient.isImpersonating()) {
          apiClient.exitImpersonation();
        } else {
          localStorage.removeItem('accessToken');
          navigate('/login');
        }
        return;
      }
      setMe(meRes.data.user || null);
      const mustChange = Boolean(meRes.data?.user?.mustChangePassword);
      setForcePwChange(mustChange);
      if (mustChange) setPwModal(true);
      setMyIp(String(ipRes.data?.ip || '').replace(/^::ffff:/, '').trim());
      setIpAllowed(Boolean(checkIpRes.data?.allowed));
      setAttendanceProfile(checkIpRes.ok ? (checkIpRes.data?.me || null) : null);
      setIpCheckInfo({
        mismatch_message: checkIpRes.data?.mismatch_message || '',
        guidance:         checkIpRes.data?.guidance         || '',
        expected_ip:      checkIpRes.data?.expected_ip      || null,
        matched_ip:       checkIpRes.data?.matched_ip       || null,
      });
      setToday(todayRes.ok ? todayRes.data : null);
      setTaskCodes(taskRes.ok ? taskRes.data?.taskCodes       || [] : []);
      setFeatureSetNames(taskRes.ok ? taskRes.data?.featureSetNames || [] : []);
      if (permRes.ok && Array.isArray(permRes.data?.permCodes)) {
        setPermCodesSet(new Set(permRes.data.permCodes));
      }
      // 권한 로딩 완료 → URL 탭 복원 및 권한 가드 동작 시작
      setPermissionsLoaded(true);
    } finally {
      setBaseLoading(false);
    }
  }

  /** 권한만 재조회 (배정/해제 후 탭 즉시 반영용) */
  async function refreshPermissions() {
    const permRes = await apiClient.get('/api/my/permissions');
    if (permRes.ok && Array.isArray(permRes.data?.permCodes)) {
      setPermCodesSet(new Set(permRes.data.permCodes));
    }
  }

  /** 출퇴근 기록 조회 */
  // 기록이 있는 연도만 (연도 셀렉트용). 실패하면 빈 배열 — 화면이 기존 목록으로 폴백한다.
  async function fetchAttendanceYears() {
    const r = await apiClient.get('/api/attendance/me/years');
    setAttendanceYears(r.ok && Array.isArray(r.data?.years) ? r.data.years : []);
  }

  async function fetchAttendanceRecords(year, month) {
    setAttendanceLoading(true);
    const result = await apiClient.get(`/api/attendance/me?year=${year}&month=${month}`);
    setAttendanceLoading(false);
    if (!result.ok) {
      showToast(result.data?.message || '출퇴근 기록 조회에 실패했습니다.', 'error');
      setAttendanceList([]);
      return;
    }
    const rows = Array.isArray(result.data) ? result.data : [];
    setAttendanceList(rows);
    setExpandedAttendance({});
    setBreakDraftById(rows.reduce((acc, row) => {
      acc[row.id] = row.break_minutes ?? '';
      return acc;
    }, {}));
  }

  /** 현재 위치(주소) 조회 — dashboardGeo.js의 fetchCurrentAddress에 위임 */
  async function fetchCurrentAddress() {
    return fetchAddressGeo({ setAddressLoading, setLocationUsable, setCurrentAddress });
  }

  /** 연차 전체 내역 조회 */
  async function fetchLeaveAll() {
    setLeaveLoading(true);
    const result = await apiClient.get('/api/leaves/me?scope=all');
    setLeaveLoading(false);
    if (!result.ok) {
      showToast(result.data?.message || '연차 데이터를 불러오지 못했습니다.', 'error');
      setLeaveData({
        profile: { name: '', hire_date: null },
        summary: { generated: 0, used: 0, paid_recognized: 0, settled: 0, remaining: 0, unpaid: 0 },
        list: [],
      });
      setLeaveLoaded(true);
      setActiveLeaveFilters([]);
      return;
    }
    setLeaveData({
      profile: result.data?.profile || { name: '', hire_date: null },
      summary: result.data?.summary || { generated: 0, used: 0, paid_recognized: 0, settled: 0, remaining: 0, unpaid: 0 },
      list:    Array.isArray(result.data?.list) ? result.data.list : [],
    });
    setLeaveLoaded(true);
    setExpandedLeave({});
    setActiveLeaveFilters([]);
  }

  return {
    // 현재 날짜
    currentYear, currentMonth,
    // UI 상태
    isImpersonating, menu, setMenu, openMenu, isMobile, accountMenuRef, accountMenuOpen, setAccountMenuOpen,
    // 외부 회사
    externalCompanies, extCompaniesLoaded,
    // 권한
    permCodesSet, taskCodes, featureSetNames, me,
    myIp, ipAllowed, ipCheckInfo, today, baseLoading, permissionsLoaded, actionLoading, setActionLoading,
    // 출퇴근
    attendanceYear, setAttendanceYear, attendanceMonth, setAttendanceMonth,
    attendanceList, setAttendanceList, attendanceLoading,
    expandedAttendance, setExpandedAttendance,
    breakDraftById, setBreakDraftById, savingBreakById, setSavingBreakById,
    // 연차
    leaveData, setLeaveData, leaveLoading, leaveLoaded, setLeaveLoaded,
    expandedLeave, setExpandedLeave, activeLeaveFilters, setActiveLeaveFilters,
    // 위치
    currentAddress, addressLoading, locationUsable,
    // 비밀번호 모달
    pwModal, setPwModal, forcePwChange, setForcePwChange, pwForm, setPwForm,
    // 지각 사유 모달
    lateReasonModalOpen, setLateReasonModalOpen, lateReasonText, setLateReasonText,
    latePromptInfo, setLatePromptInfo,
    // 옵션 배열
    yearOptions, monthOptions,
    // 함수
    attendanceProfile, attendanceYears, fetchAttendanceYears,
    fetchBase, fetchCurrentAddress, refreshPermissions, fetchAttendanceRecords, fetchLeaveAll,
  };
}
